import { useEffect, useRef } from "react";

/**
 * useCanvasBroadcast — generate a MediaStream for audio-only DJ studios.
 *
 * Combines:
 *   - The engine's existing audio MediaStream (EQ/FX/xfader/mic applied).
 *   - A 720p canvas we render every frame with:
 *       • Tap IN Radio logo + station name + host name
 *       • Current deck A / B covers + track titles
 *       • Live waveform visualization
 *       • "LIVE" badge with elapsed time
 *
 * The returned MediaStream has ONE video track (from canvas.captureStream)
 * and ONE audio track (from the engine).  Safe to feed to MediaRecorder +
 * the restream WebSocket.
 */
export default function useCanvasBroadcast({ audioStreamRef, meta }) {
  const canvasRef = useRef(null);
  const combinedRef = useRef(null);
  const rafRef = useRef(null);
  const metaRef = useRef(meta);
  useEffect(() => { metaRef.current = meta; }, [meta]);

  // Lazy-init canvas + stream
  const getStream = () => {
    if (combinedRef.current) return combinedRef.current;
    if (!canvasRef.current) {
      const c = document.createElement("canvas");
      c.width = 1280; c.height = 720;
      canvasRef.current = c;
    }
    const cs = canvasRef.current.captureStream(30);
    const audio = audioStreamRef?.current;
    const combined = new MediaStream();
    cs.getVideoTracks().forEach((t) => combined.addTrack(t));
    if (audio) audio.getAudioTracks().forEach((t) => combined.addTrack(t));
    combinedRef.current = combined;
    startRenderLoop();
    return combined;
  };

  const startRenderLoop = () => {
    if (rafRef.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const startTs = Date.now();

    const drawCover = (x, y, size, url) => {
      if (!url) {
        ctx.fillStyle = "#1a1f2c";
        ctx.fillRect(x, y, size, size);
        return;
      }
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = url;
      img.onload = () => { try { ctx.drawImage(img, x, y, size, size); } catch {} };
      ctx.fillStyle = "#1a1f2c";
      ctx.fillRect(x, y, size, size);
    };

    const paintOnce = () => {
      const m = metaRef.current || {};
      // Dark gradient background
      const bg = ctx.createLinearGradient(0, 0, 1280, 720);
      bg.addColorStop(0, "#06080f"); bg.addColorStop(1, "#10131c");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, 1280, 720);

      // Title bar
      ctx.fillStyle = "#f43f5e";
      ctx.font = "900 72px system-ui,-apple-system,sans-serif";
      ctx.fillText("TAP IN", 60, 110);
      ctx.fillStyle = "#eab308";
      ctx.fillText("RADIO", 290, 110);
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = "700 28px system-ui,-apple-system,sans-serif";
      ctx.fillText(m.stationName || "Live show", 60, 150);

      // LIVE badge
      const el = Math.floor((Date.now() - startTs) / 1000);
      const mm = String(Math.floor(el / 60)).padStart(2, "0");
      const ss = String(el % 60).padStart(2, "0");
      ctx.fillStyle = "#ef4444";
      ctx.fillRect(1040, 60, 180, 56);
      ctx.fillStyle = "#fff";
      ctx.font = "900 24px system-ui,-apple-system,sans-serif";
      ctx.fillText(`● LIVE  ${mm}:${ss}`, 1052, 97);

      // Two deck covers
      drawCover(80, 220, 360, m.deckA?.cover_url);
      drawCover(840, 220, 360, m.deckA || m.deckB ? (m.deckB?.cover_url) : null);

      // Deck labels
      ctx.fillStyle = "#38bdf8"; ctx.font = "900 40px system-ui,-apple-system,sans-serif";
      ctx.fillText("DECK A", 80, 210);
      ctx.fillStyle = "#fb7185"; ctx.fillText("DECK B", 840, 210);

      // Track titles
      ctx.fillStyle = "#fff"; ctx.font = "700 28px system-ui,-apple-system,sans-serif";
      const truncate = (s, n) => { if (!s) return "— no track —"; return s.length > n ? s.slice(0, n - 1) + "…" : s; };
      ctx.fillText(truncate(m.deckA?.title, 24), 80, 620);
      ctx.fillStyle = "rgba(255,255,255,0.65)"; ctx.font = "500 22px system-ui,-apple-system,sans-serif";
      ctx.fillText(truncate(m.deckA?.artist, 28), 80, 655);
      ctx.fillStyle = "#fff"; ctx.font = "700 28px system-ui,-apple-system,sans-serif";
      ctx.fillText(truncate(m.deckB?.title, 24), 840, 620);
      ctx.fillStyle = "rgba(255,255,255,0.65)"; ctx.font = "500 22px system-ui,-apple-system,sans-serif";
      ctx.fillText(truncate(m.deckB?.artist, 28), 840, 655);

      // On-air indicator
      const onAir = (m.xfader ?? 0.5) < 0.5 ? "A" : "B";
      ctx.fillStyle = onAir === "A" ? "#38bdf8" : "#fb7185";
      ctx.font = "900 36px system-ui,-apple-system,sans-serif";
      ctx.fillText("◀  ON AIR  ▶", 520, 400);
      ctx.fillStyle = "#fff"; ctx.font = "900 140px system-ui,-apple-system,sans-serif";
      ctx.textAlign = "center"; ctx.fillText(onAir, 640, 520); ctx.textAlign = "left";

      // Host name
      if (m.hostName) {
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.font = "700 20px system-ui,-apple-system,sans-serif";
        ctx.fillText(`HOSTED BY ${m.hostName.toUpperCase()}`, 60, 690);
      }
    };

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      paintOnce();
    };
    loop();
  };

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { combinedRef.current?.getTracks()?.forEach((t) => t.stop()); } catch {}
    combinedRef.current = null;
  };

  useEffect(() => () => stop(), []);

  return { getStream, stop };
}
